import { Text } from '@wfp/react';
import sortBy from 'lodash/sortBy';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { CRF23 } from 'src/constants';
import { changeViewStateAction } from 'src/redux/view/actions';
import { SECTION_DETAIL_DATA_KEY } from 'src/redux/view/constants';
import CommentListFilterSet from './CommentListFilterSet';
import CommentListItem from './CommentListItem';

export const CommentList = ({
  user,
  permissions,
  sectionAPIKwargs,
  commentsContainerID,
  deleteComment,
  comments,
}) => {
  const [filters, setFilters] = useState([]);

  const viewData = useSelector(state => state.viewData);
  const is2023CRF = viewData.ACR_REPORTS_DETAIL?.data?.crf === CRF23;

  const multiNarratives = viewData.ACR_SECTIONS_DETAIL?.data?.narratives;
  const narrativeContent = useSelector(
    state => state.viewData[SECTION_DETAIL_DATA_KEY]?.data?.content,
  );
  const highlightComment = useSelector(
    state => state.viewState?.highlightComment,
  );

  const scrollSharedOptions = {
    duration: 800,
    delay: 0,
    smooth: 'easeInOutQuart',
    ignoreCancelEvents: true,
  };

  const dispatch = useDispatch();

  function removeHighlight() {
    dispatch(
      changeViewStateAction({
        highlightComment: undefined,
      }),
    );
  }

  useEffect(() => {
    if (!highlightComment) return;

    let timer;

    // Helper function to find comment or reply in the comments tree
    const findCommentOrReply = (comments, targetId) => {
      for (const comment of comments) {
        // Check main comment
        if (comment.pk.toString() === targetId) {
          return {
            comment,
            isReply: false,
            scrollId: comment.anchor || `comment-${comment.pk}`,
          };
        }

        // Check replies
        if (comment.comments && comment.comments.length > 0) {
          for (const reply of comment.comments) {
            if (reply.pk.toString() === targetId) {
              return {
                comment: reply,
                isReply: true,
                scrollId: `comment-${reply.pk}`, // replies use comment-ID format
              };
            }
          }
        }
      }
      return null;
    };

    // Helper function to find by anchor (for narrative anchors)
    const findByAnchor = (comments, targetAnchor) => {
      for (const comment of comments) {
        if (comment.anchor === targetAnchor) {
          return {
            comment,
            isReply: false,
            scrollId: targetAnchor,
          };
        }
      }
      return null;
    };

    // Try to find comment/reply by ID first (for URLs like #Overview?comment=43049 or #Overview?reply=43049)
    let foundItem = findCommentOrReply(orderedComments, highlightComment);

    // If not found by ID, try to find by anchor (for narrative anchors)
    if (!foundItem) {
      foundItem = findByAnchor(orderedComments, highlightComment);
    }

    if (foundItem) {
      timer = setTimeout(() => {
        removeHighlight();
      }, 5000);

      // Check if this is a reply inside a closed comment
      const isReplyInClosedComment =
        foundItem.isReply &&
        orderedComments.find(
          comment =>
            comment.comments?.some(
              reply => reply.pk.toString() === highlightComment,
            ) && comment.closed,
        );

      // Add delay for scrolling if the target is in a closed comment that needs to be expanded
      const scrollDelay = isReplyInClosedComment ? 200 : 0;

      setTimeout(() => {
        // Scroll the main wrapper only if the page hasn't been scrolled
        if (document.documentElement.scrollTop === 0) {
          scroller.scrollTo('section-wrapper-main', scrollSharedOptions);
        }

        // Scroll to the found comment or reply
        scroller.scrollTo(foundItem.scrollId, {
          ...scrollSharedOptions,
          containerId: 'acr-comments-wrapper',
          offset: -50,
        });
      }, scrollDelay);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [highlightComment, orderedComments, comments]);

  function toggleFilter(filterToToggle) {
    const index = filters.indexOf(filterToToggle);
    const draftFilters = [...filters];
    if (index > -1) {
      draftFilters.splice(index, 1);
    } else {
      draftFilters.push(filterToToggle);
    }

    setFilters(draftFilters);
  }

  function filterComments(data) {
    if (filters.includes('opened')) {
      data = data.filter(comment => !comment.closed);
    }
    if (filters.includes('important')) {
      data = data.filter(comment => comment.important);
    }
    if (filters.includes('owner')) {
      data = data.filter(comment => comment.user.username === user);
    }
    return data;
  }

  function getAnchorsList(content) {
    if (!content) return [];

    let narrativeArray = content.split('id="');
    narrativeArray.shift();
    narrativeArray = narrativeArray.map(item => item.split('" name', 1)[0]);
    return narrativeArray;
  }

  function getSortedLinkedComments(linkedComments = [], anchorsList = []) {
    return linkedComments.sort(
      (a, b) => anchorsList.indexOf(a.anchor) - anchorsList.indexOf(b.anchor),
    );
  }

  // Ordering comments based on their highlights order in narrative
  const orderedComments = useMemo(() => {
    const filteredComments = filterComments(comments);

    let unlinkedComments = [];
    let linkedComments = [];

    if (!is2023CRF) {
      const narrativeArray = getAnchorsList(narrativeContent);
      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          linkedComments.push(comment);
        } else {
          unlinkedComments.push(comment);
        }
      });

      linkedComments = getSortedLinkedComments(linkedComments, narrativeArray);

      return [...linkedComments, ...unlinkedComments];
    }

    const multiNarrativesOrdered = sortBy(multiNarratives, 'order');
    multiNarrativesOrdered.forEach(narrative => {
      let multiNarrativeLinkedComments = [];
      const narrativeArray = getAnchorsList(narrative.content);

      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          multiNarrativeLinkedComments.push({
            ...comment,
            narrativeLinkedTitle: narrative.title,
          });
        }
      });

      multiNarrativeLinkedComments = getSortedLinkedComments(
        multiNarrativeLinkedComments,
        narrativeArray,
      );

      linkedComments.push(...multiNarrativeLinkedComments);
    });

    unlinkedComments = filteredComments
      .filter(
        item => linkedComments.findIndex(comm => comm.pk === item.pk) === -1,
      )
      .map(comment => ({
        ...comment,
        narrativeLinkedTitle: 'Untagged comments',
      }));

    return [...linkedComments, ...unlinkedComments];
  }, [narrativeContent, comments, filters]);

  // Helper function to check if a comment should be highlighted (not its replies)
  const shouldHighlight = comment => {
    return (
      comment.anchor === highlightComment ||
      comment.pk.toString() === highlightComment
    );
  };

  return (
    <>
      <CommentListFilterSet toggleFilter={toggleFilter} />
      {orderedComments?.map((comment, index) => (
        <Fragment key={comment.pk}>
          {comment.narrativeLinkedTitle &&
            (index === 0 ||
              orderedComments[index - 1].narrativeLinkedTitle !==
                comment.narrativeLinkedTitle) && (
              <div className="comment-item-linked-narrative-title">
                <Text kind="h6">{comment.narrativeLinkedTitle}</Text>
              </div>
            )}
          <CommentListItem
            activeUser={user}
            permissions={permissions}
            sectionAPIKwargs={sectionAPIKwargs}
            commentsContainerID={commentsContainerID}
            id={comment.pk}
            data={comment}
            deleteComment={deleteComment}
            highlightIsHovered={shouldHighlight(comment)}
            highlightComment={highlightComment}
          />
        </Fragment>
      ))}
    </>
  );
};

export default CommentList;
